import { useDrag } from '@use-gesture/react';
import { ScaleLoader } from 'react-spinners';
import { useCallback, useContext, useState } from 'react';
import { ApiProvider } from '@leverege/api-redux';
import S from './PTZDragTool.css';
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
export default function PTZDragTool({
  parentHeight,
  parentWidth,
  parentRef,
  context
}) {
  const minDimension = Math.min(parentHeight, parentWidth);
  const radius = minDimension / 12;
  const cx = parentWidth / 2;
  const cy = parentHeight / 2;
  const {
    clientProps: {
      delegate
    }
  } = context;
  const [loading, setLoading] = useState(false);
  return /*#__PURE__*/_jsx("div", {
    className: S.container,
    children: loading ? /*#__PURE__*/_jsx(ScaleLoader, {
      color: "orange"
    }) : /*#__PURE__*/_jsxs("svg", {
      className: S.svg,
      width: parentWidth,
      height: parentHeight,
      viewBox: `0 0 ${parentWidth} ${parentHeight}`,
      children: [/*#__PURE__*/_jsx(PanTilt, {
        cx: cx,
        cy: cy,
        delegate: delegate,
        parentHeight: parentHeight,
        parentWidth: parentWidth,
        parentRef: parentRef,
        radius: radius,
        setLoading: setLoading
      }), /*#__PURE__*/_jsx(Zoom, {
        cx: cx,
        cy: cy,
        delegate: delegate,
        radius: radius,
        setLoading: setLoading
      })]
    })
  });
}
function PanTilt({
  cx,
  cy,
  delegate,
  parentHeight,
  parentRef,
  parentWidth,
  radius,
  setLoading
}) {
  const plusSize = 0.1 * radius;
  const strokeWidth = 0.02 * radius;
  const offset = {
    top: parentRef.current?.getBoundingClientRect().top,
    left: parentRef.current?.getBoundingClientRect().left
  };
  const api = useContext(ApiProvider.Context);
  const [dragCoordinates, setDragCoordinates] = useState();
  const [currentPT, setCurrentPT] = useState();
  const onDrag = useCallback(async state => {
    const {
      xy,
      event
    } = state;
    event.preventDefault();
    event.stopPropagation();
    const offsets = [xy[0] - cx, xy[1] - cy];
    const [x, y] = offsets;
    const pctX = clamp(x / (parentWidth / 2), -1, 1) * 0.1;
    const pctY = clamp(y / (parentHeight / 2), -1, 1) * 0.1;
    let pan, tilt;
    if (Math.abs(pctX) > 0.01) {
      pan = pctX * 360;
    }
    if (Math.abs(pctY) > 0.01) {
      tilt = -(pctY * 360);
    }
    if (state.dragging) {
      setDragCoordinates(xy);
      setCurrentPT([pan, tilt]);
    } else {
      setDragCoordinates(null);
      setCurrentPT(null);
      if (pan || tilt) {
        const interfaceUrl = delegate.getInterfaceUrl();
        const body = {};
        if (pan) {
          body.rPan = pan;
        }
        if (tilt) {
          body.rTilt = tilt;
        }
        setLoading(true);
        try {
          await api.child(`v1/interface/${interfaceUrl}.srv/streamProxy/ptz`).patchRaw(body);
        } catch (err) {
          console.error(err);
        }
        setLoading(false);
      }
    }
  }, [cx, cy, parentWidth, parentHeight, delegate, api, setLoading]);
  const bindDrag = useDrag(onDrag, {
    transform: ([x, y]) => {
      return [x - offset.left, y - offset.top];
    }
  });
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsxs("defs", {
      children: [/*#__PURE__*/_jsx("marker", {
        id: "arrow",
        viewBox: "0 0 10 10",
        refX: "10",
        refY: "5",
        markerWidth: "6",
        markerHeight: "6",
        orient: "auto-start-reverse",
        children: /*#__PURE__*/_jsx("path", {
          stroke: "orange",
          fill: "orange",
          d: "M 0 0 L 10 5 L 0 10 z"
        })
      }), /*#__PURE__*/_jsxs("filter", {
        x: "0",
        y: "0",
        width: "1",
        height: "1",
        id: "solid",
        children: [/*#__PURE__*/_jsx("feFlood", {
          floodColor: "black"
        }), /*#__PURE__*/_jsx("feComposite", {
          in: "SourceGraphic",
          operator: "xor"
        })]
      })]
    }), /*#__PURE__*/_jsx("circle", {
      cx: cx,
      cy: cy,
      r: radius,
      stroke: "orange",
      fill: "transparent",
      strokeWidth: strokeWidth,
      ...bindDrag()
    }), /*#__PURE__*/_jsx("line", {
      stroke: "orange",
      strokeWidth: strokeWidth,
      x1: cx - plusSize,
      x2: cx + plusSize,
      y1: cy,
      y2: cy
    }), /*#__PURE__*/_jsx("line", {
      stroke: "orange",
      strokeWidth: strokeWidth,
      x1: cx,
      x2: cx,
      y1: cy - plusSize,
      y2: cy + plusSize
    }), dragCoordinates && /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsx("line", {
        markerEnd: "url(#arrow)",
        stroke: "orange",
        strokeWidth: strokeWidth,
        x1: cx,
        x2: dragCoordinates[0],
        y1: cy,
        y2: dragCoordinates[1]
      }), currentPT?.[0] && /*#__PURE__*/_jsxs(_Fragment, {
        children: [/*#__PURE__*/_jsxs("text", {
          filter: "url(#solid)",
          textAnchor: "middle",
          x: cx,
          y: cy - radius - 40,
          children: ["Pan: ", currentPT[0].toFixed(2), "\xB0"]
        }), /*#__PURE__*/_jsxs("text", {
          fill: "orange",
          textAnchor: "middle",
          x: cx,
          y: cy - radius - 40,
          children: ["Pan: ", currentPT[0].toFixed(2), "\xB0"]
        })]
      }), currentPT?.[1] && /*#__PURE__*/_jsxs(_Fragment, {
        children: [/*#__PURE__*/_jsxs("text", {
          filter: "url(#solid)",
          textAnchor: "middle",
          x: cx,
          y: cy - radius - 20,
          children: ["Tilt: ", currentPT[1].toFixed(2), "\xB0"]
        }), /*#__PURE__*/_jsxs("text", {
          fill: "orange",
          textAnchor: "middle",
          x: cx,
          y: cy - radius - 20,
          children: ["Tilt: ", currentPT[1].toFixed(2), "\xB0"]
        })]
      })]
    })]
  });
}
function Zoom({
  cx,
  cy,
  delegate,
  hasDigitalZoom = true,
  radius,
  setLoading
}) {
  const maxZoom = hasDigitalZoom ? 19999 : 9999;
  const zoomThumbRadius = radius * 0.3 / 2;
  const [thumbY, setThumbY] = useState(0);
  const [zoomPct, setZoomPct] = useState();
  const api = useContext(ApiProvider.Context);
  const onDrag = useCallback(async state => {
    const {
      movement,
      dragging,
      event
    } = state;
    event.preventDefault();
    event.stopPropagation();
    const [, y] = movement;
    const isNegative = y < 0;
    let value = Math.min(Math.abs(y), radius);
    value = isNegative ? -value : value;
    const pct = clamp(value / radius, -1, 1) * 0.5;
    let rZoom, pctFormatted;
    if (Math.abs(pct) > 0.05) {
      rZoom = -(pct * maxZoom);
      pctFormatted = -(100 * pct);
    }
    if (dragging) {
      setThumbY(value);
      setZoomPct(pctFormatted);
    } else {
      setThumbY(0);
      setZoomPct(null);
      if (rZoom) {
        const interfaceUrl = delegate.getInterfaceUrl();
        const body = {
          rZoom
        };
        setLoading(true);
        try {
          await api.child(`v1/interface/${interfaceUrl}.srv/streamProxy/ptz`).patchRaw(body);
        } catch (err) {
          console.error(err);
        }
        setLoading(false);
      }
    }
  }, [radius, maxZoom, api, delegate, setLoading]);
  const bindDrag = useDrag(onDrag, {
    axis: 'y'
  });
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx("rect", {
      stroke: "orange",
      fill: "transparent",
      x: cx + radius * 1.2,
      y: cy - radius * 1.1,
      height: radius * 2.2,
      width: radius * 0.3,
      rx: 8
    }), /*#__PURE__*/_jsx("circle", {
      className: S.zoomThumb,
      cx: cx + radius * 1.2 + zoomThumbRadius,
      cy: cy + thumbY,
      r: radius * 0.3 / 2,
      fill: "orange",
      ...bindDrag()
    }), zoomPct && /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsxs("text", {
        filter: "url(#solid)",
        textAnchor: "middle",
        x: cx,
        y: cy - radius - 20,
        children: ["Zoom: ", zoomPct.toFixed(2), "%"]
      }), /*#__PURE__*/_jsxs("text", {
        fill: "orange",
        textAnchor: "middle",
        x: cx,
        y: cy - radius - 20,
        children: ["Zoom: ", zoomPct.toFixed(2), "%"]
      })]
    })]
  });
}
function clamp(value, min, max) {
  return Math.min(Math.max(value, min), max);
}